// import Filter from "@/components/ui/filters";
import { useAuthorization } from "@/hooks/use-authorization";
import { useGetProcess } from "@/queries/process";
import {
  filterTypesEnum,
  sortDirectionEnum,
  type FilterOption,
  type SortDirectionType,
  type SortOptionsTypes,
} from "@/types/common/filters";
import { formatToRelativeTime } from "@/utils/dates";
import { Permissions } from "@/utils/permissions";
import {
  Badge,
  Body,
  Button,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
// import type { SortingState } from "@tanstack/react-table";
// import { parseAsString, useQueryState } from "nuqs";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import DuplicateFlowModal from "./DuplicateFlowModal";
import { useNavigate } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetFlowsByProcessId,
  useStoreFlow,
  useUpdateFlowStatus,
} from "@/queries/flows/flows.api";
import {
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from "nuqs";
import { useDebounceValue } from "usehooks-ts";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import NewFlowModal from "./NewFlowModal";
import { useGetMe } from "@/queries/me";
import { EditTDSModal } from "./EditTDSModal";
// import { processStatusEnum } from "@/types/entities/process";
// import { getStatusBadgeColor } from "@/utils/process-ui";
import type { AxiosError } from "axios";
import { PulseIndicator } from "@/components/common/pulse-indicator";
import SearchInput from "@/components/common/search-input";
import NewParentFlowModal from "./NewParentFlowModal";
import { Renderify } from "renderify-js";
import SortDropdown from "./SortDropdown";
import ParentFlowCard from "./flows/parent-flow";
import { PortalDetailsModal } from "./PortalDetailsModal";
import { useQueryClient } from "@tanstack/react-query";
import DeleteFlowModal from "./DeleteFlowModal";
import { VersionModal } from "./VersionModal";
import NewFlowVersionModal from "./NewFlowVersionModal";
import RestoreVersionModal from "./RestoreVersionModal";
import EditSubFlowModal from "./EditSubFlowModal";
import type { FlowsType } from "@/types/entities/flows";
import ResolutionComment from "./ResolutionComment";

interface DetalleProps {
  processId: string | undefined;
  selectedVersion: number | null;
  setSelectedVersion: (version: number | null) => void;
  latestVersion: number;
  onFlowsChange?: (flows: FlowsType[]) => void;
  setIsWaitingForVersion?: (waiting: boolean) => void;
  currentRevision?: {
    id: string;
    status: "APPROVED" | "DENIED" | "PENDING" | "IN_PROCESS";
    comments?: {
      id: string;
      text: string;
      created_by: string;
      created_at: string;
    }[];
    created_at: string;
    updated_at: string;
  } | null;
  isVersionLocked?: boolean;
  duplicatingFlow?: {
    id: string;
    version_number?: number;
  } | null;
  isLoadingMainFlows?: boolean;
}
interface ApiErrors {
  name?: string[];
}
interface ApiErrorResponse {
  errors?: ApiErrors;
}

export type ModalStateType =
  | "idle"
  | "filters"
  | "tds"
  | "duplicate-flow"
  | "new-parent-flow"
  | "new-flow"
  | "edit-subflow"
  | "details"
  | "versioning"
  | "new-version"
  | "delete"
  | "restore-version";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const sortFields: SortOptionsTypes[] = [
  { label: "Nombre", value: "name" },
  { label: "Fecha de actualización", value: "updated_at" },
  { label: "Fecha de creación", value: "created_at" },
];

const getFlowStatusLabel = (status?: string) => {
  switch (status) {
    case "DRAFT":
    case "IN_PROCESS":
      return "En construcción";
    case "PUBLISHED":
      return "Activa";
    case "DEPRECATED":
      return "Archivado";
    default:
      return "Sin flujos";
  }
};

const getFlowStatusBadgeColor = (status?: string) => {
  switch (status) {
    case "DRAFT":
    case "IN_PROCESS":
      return "action";
    case "PUBLISHED":
      return "success";
    case "DEPRECATED":
      return "danger";
    default:
      return "neutral";
  }
};

export const Detalle: React.FC<DetalleProps> = ({
  processId,
  selectedVersion,
  setSelectedVersion,
  latestVersion,
  onFlowsChange,
  setIsWaitingForVersion,
  currentRevision,
  isVersionLocked = false,
  duplicatingFlow = null,
  isLoadingMainFlows = false,
}) => {
  //const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [flowToRestore, setFlowToRestore] = useState<string | null>(null);
  const [flowToEdit, setFlowToEdit] = useState<string | null>(null);

  const { showToast } = useToast();
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.processes.update);
  const canCreateFlow = hasPermission(Permissions.flows.create);
  const canViewFlow = hasPermission(Permissions.flows.read);

  // const [sorting, setSorting] = React.useState<SortingState>([]);
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [query, setQuery] = useQueryState(
    "query",
    parseAsString.withDefault(""),
  );
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault(""),
  );
  const [search, setSearch] = React.useState<string | null>(query);
  const [deferredQuery] = useDebounceValue(search || "", 500);
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("created_at"),
  );
  const [orderDirection, setOrderDirection] = useQueryState(
    "order_direction",
    parseAsStringEnum([
      sortDirectionEnum.asc,
      sortDirectionEnum.desc,
    ]).withDefault(sortDirectionEnum.desc),
  );

  const { data } = useGetMe();
  const {
    mutateAsync: storeFlow,
    isPending: isLoadingStoreFlow,
    error,
  } = useStoreFlow();

  const handleCreate = async ({
    name,
    extension_count,
  }: {
    name: string;
    extension_count: number;
  }) => {
    if (!processId) return;
    if (!data?.data) return;
    const response = await storeFlow({
      process_id: processId,
      created_by: data?.data.id,
      status: "DRAFT",
      active: true,
      name,
      type: "FORMALITIES",
      max_time_extensions: extension_count,
    });
    if (response.data.id) {
      navigate(`/procesos/${processId}/flujo/${response.data.id}`);
    }
  };

  const handleCreateSubFlow = async ({
    name,
    parent_flow_id,
  }: {
    name: string;
    parent_flow_id: string;
  }) => {
    if (!processId) return;
    if (!data?.data) return;
    const response = await storeFlow({
      process_id: processId,
      created_by: data?.data.id,
      status: "DRAFT",
      active: true,
      name,
      type: "FORMALITIES",
      parent_flow_id,
    });
    if (response.data.id) {
      navigate(`/procesos/${processId}/flujo/${response.data.id}`);
    }
  };

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>([
    {
      id: 1,
      field: "status",
      type: filterTypesEnum.status,
      label: "Estado del flujo",
      isUpdated: !!status,
      result: status || "Activo",
      status: [
        {
          field: "pending",
          label: "Pendiente de aprobar",
          value: status.includes("pending"),
        },
        {
          field: "draft",
          label: "Borrador",
          value: status.includes("draft"),
        },
        {
          field: "publish",
          label: "Publicado",
          value: status.includes("publish"),
        },
        {
          field: "active",
          label: "Activo",
          value: status.includes("active"),
        },
        {
          field: "inactive",
          label: "Inactivo",
          value: status.includes("inactive"),
        },
      ],
    },
  ]);

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  const {
    data: flows,
    isFetching: isLoadingFlows,
    isError,
  } = useGetFlowsByProcessId(
    processId,
    {
      page: page || 1,
      perPage: 20,
      search: deferredQuery,
      ...(status ? { status } : {}),
      order_by: `${orderDirection === "desc" ? "!" : ""}${order_by || "created_at"}`,
      parent: "!",
    },
    0, //TODO: Change this for websocket updates
    canViewFlow,
  );

  //const displayFlow = flowPublished[0] || flows?.data?.[0];

  const [flowIdToDelete, setFlowIdToDelete] = useState<string | null>(null);

  const { mutateAsync: updateFlowStatus } = useUpdateFlowStatus();
  const queryClient = useQueryClient();

  // const latestVersion = useMemo(() => {
  //   if (!flows?.data?.length) return 0;
  //   const max = Math.max(...flows.data.map((f) => f.version || 0));
  //   return max;
  // }, [flows?.data]);

  // Flujos de la versión seleccionada o por defecto
  const displayFlows = useMemo(() => {
    if (!flows?.data?.length) return [];

    const version = selectedVersion ?? latestVersion;
    const filtered = flows.data.filter((f) => f.version === version);
    return filtered;
  }, [flows?.data, selectedVersion, latestVersion]);

  const allFlowsInVersion = useMemo(() => {
    if (!displayFlows.length) return [];

    const flatten = (flows: FlowsType[]): FlowsType[] => {
      return flows.reduce<FlowsType[]>((acc, flow) => {
        acc.push(flow);
        if (flow.child_flows && flow.child_flows.length > 0) {
          acc.push(...flatten(flow.child_flows as FlowsType[]));
        }
        return acc;
      }, []);
    };

    return flatten(displayFlows);
  }, [displayFlows]);

  const publishedFlow = useMemo(() => {
    if (!flows?.data?.length) return null;
    return flows.data.find((f) => f.status === "PUBLISHED") || null;
  }, [flows?.data]);

  useEffect(() => {
    onFlowsChange?.(allFlowsInVersion);
  }, [allFlowsInVersion, onFlowsChange]);

  // Para el Badge (un solo flow - el publicado o el primero de la versión)
  const displayFlowForBadge = useMemo(() => {
    if (!displayFlows.length) return null;
    return (
      displayFlows.find((f) => f.status === "PUBLISHED") || displayFlows[0]
    );
  }, [displayFlows]);

  const latestVersionFlow = useMemo(() => {
    if (!flows?.data?.length) return null;

    const latestFlows = flows.data.filter((f) => f.version === latestVersion);

    if (!latestFlows.length) return null;

    const originalFlow = latestFlows.find((f) => !f.duplicated_origin_id);

    if (originalFlow) return originalFlow;

    return latestFlows.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime(),
    )[0];
  }, [flows?.data, latestVersion]);

  // const isVersionPending =
  //   selectedVersion !== null && displayFlows.length === 0;
  const isVersionPending =
    (selectedVersion !== null && displayFlows.length === 0) ||
    !!duplicatingFlow ||
    isLoadingMainFlows;

  // useEffect(() => {
  //   if (!isVersionPending) return;

  //   const interval = setInterval(() => {
  //     queryClient.invalidateQueries({
  //       queryKey: ["flows", processId],
  //       exact: false,
  //     });
  //     queryClient.invalidateQueries({
  //       queryKey: ["main-flows"],
  //     });
  //   }, 3000);

  //   const timeout = setTimeout(() => {
  //     clearInterval(interval);
  //     setSelectedVersion(null);
  //   }, 30000);

  //   return () => {
  //     clearInterval(interval);
  //     clearTimeout(timeout);
  //   };
  // }, [
  //   isVersionPending,
  //   processId,
  //   queryClient,
  //   selectedVersion,
  //   setSelectedVersion,
  //   displayFlows,
  // ]);

  // useEffect(() => {
  //   if (!isVersionPending) return;

  //   const timeout = setTimeout(() => {
  //     showToast({
  //       title: "Aviso",
  //       description:
  //         "La versión está tardando en crearse. Refresca la página en unos momentos.",
  //       options: { type: "warning" },
  //     });
  //   }, 30000);

  //   return () => clearTimeout(timeout);
  // }, [isVersionPending, showToast]);

  const handleFlowStatusChange = async (
    flowId: string,
    status: "DRAFT" | "PUBLISHED" | "DEPRECATED",
  ) => {
    try {
      await updateFlowStatus({
        id: flowId,
        status: status,
      });
      showToast({
        title: "Flujo aprobado",
        description: "El flujo ha sido aprobado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", processId],
      });
    } catch (error) {
      console.error("Error publishing flow:", error);
      showToast({
        title: "Error",
        description: "Hubo un error al aprobar el flujo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const [modalState, setModalState] = useState<ModalStateType>("idle");

  const [duplicationType, setDuplicationType] = useState<
    "version" | "duplicate" | null
  >(null);
  const [flowToDuplicate, setFlowToDuplicate] = useState<string | null>(null);
  const handleDuplicateFlow = useCallback(
    (flowId: string, type: "version" | "duplicate" = "duplicate") => {
      setDuplicationType(type);
      setFlowToDuplicate(flowId);
      setModalState("duplicate-flow");
    },
    [],
  );

  const {
    data: process,
    isLoading,
    refetch: refetchProcess,
  } = useGetProcess(processId);

  const userName = process?.data?.createdBy?.name
    ? `${process?.data?.createdBy?.name ?? ""} ${process?.data?.createdBy?.last_name ?? ""}`
    : "Desconocido";

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters([
      {
        id: 1,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado del flujo",
        isUpdated: !!status,
        result: status || "Activo",
        status: [
          {
            field: "pending",
            label: "Pendiente de aprobar",
            value: status.includes("pending"),
          },
          {
            field: "draft",
            label: "Borrador",
            value: status.includes("draft"),
          },
          {
            field: "publish",
            label: "Inactivo",
            value: status.includes("Inactivo"),
          },
          {
            field: "active",
            label: "Activo",
            value: status.includes("active"),
          },
          {
            field: "inactive",
            label: "Inactivo",
            value: status.includes("inactive"),
          },
        ],
      },
    ]);
    setStatus("active");
    setModalState("idle");
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "status") {
          setStatus(f.result || null);
        }
        setPage(1);
      }
    });

    //set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const filtersCount = formatFilters.filter((f) => f.isUpdated).length;

  // is empty if data is empty array and filters are not applied
  const isEmpty =
    flows?.data &&
    flows.data.length === 0 &&
    filtersCount === 0 &&
    !deferredQuery;

  // has active search/filters
  const hasActiveSearch = filtersCount > 0 || !!deferredQuery;

  const handleapplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setPage(1);
    setOrderBy(field || null);
    setOrderDirection(direction);
  };

  return (
    <>
      {modalState === "new-flow" && (
        <NewFlowModal
          open
          onClose={() => setModalState("idle")}
          onSubmit={(data) => handleCreateSubFlow(data)}
          isLoading={isLoadingStoreFlow}
          error={error as AxiosError<ApiErrorResponse> | undefined}
          processId={processId!}
          currentVersion={selectedVersion ?? latestVersion}
        />
      )}
      {modalState === "new-parent-flow" && (
        <NewParentFlowModal
          open
          onClose={() => {
            setModalState("idle");
          }}
          onSubmit={(data) => handleCreate(data)}
          isLoading={isLoadingStoreFlow}
          error={error as AxiosError<ApiErrorResponse> | undefined}
        />
      )}

      {modalState === "tds" && (
        <EditTDSModal
          isOpen={modalState === "tds"}
          processId={processId}
          files={process?.data?.files || []}
          onClose={() => setModalState("idle")}
          onSuccess={() => {
            refetchProcess();
            setModalState("idle");
          }}
        />
      )}

      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}

      {modalState === "versioning" && (
        <VersionModal
          isOpen={modalState === "versioning"}
          process={process}
          onClose={() => setModalState("idle")}
          onSuccess={() => {
            refetchProcess();
            setModalState("idle");
          }}
          setModalType={setModalState}
          onSelectVersion={(version) => {
            setSelectedVersion(version);
          }}
          isVersionPending={isVersionPending}
          pendingVersion={selectedVersion}
          onRestore={(flowId) => {
            setFlowToRestore(flowId);
            setModalState("restore-version");
          }}
        />
      )}

      <PortalDetailsModal
        isOpen={modalState === "details"}
        onClose={() => setModalState("idle")}
      />

      {/* Encabezado del proceso */}
      {isLoading ? (
        <div role="status" className="max-w-sm animate-pulse pt-6">
          <Skeleton className="h-3 rounded-full w-48 mb-4" />
          <Skeleton className="h-2 rounded-full max-w-95 mb-2.5 mt-6" />
        </div>
      ) : (
        <div className="flex flex-col gap-2 my-6" data-testid="process-detail">
          <div className="inline-flex items-center flex-wrap gap-4">
            <div className="inline-flex items-center">
              {/* <Badge
                size="sm"
                variant={
                  isVersionPending
                    ? "neutral"
                    : displayFlowForBadge
                      ? getFlowStatusBadgeColor(displayFlowForBadge?.status)
                      : "action"
                }
                onClick={() => {
                  if (canEdit && canViewFlow) {
                    setModalState("versioning");
                  }
                }}
                className={
                  publishedFlow &&
                  publishedFlow.version !== displayFlowForBadge?.version
                    ? "rounded-r-none"
                    : ""
                }
              >
                {isVersionPending
                  ? `Creando v${selectedVersion}...`
                  : displayFlowForBadge
                    ? `${getFlowStatusLabel(displayFlowForBadge.status)} v${displayFlowForBadge.version || 1}${
                        displayFlowForBadge.duplicated_origin_id &&
                        flows?.data?.find(
                          (f) =>
                            f.id === displayFlowForBadge.duplicated_origin_id,
                        )?.version !==
                          (displayFlowForBadge.version || 1) - 1
                          ? ` (Restaurada de v${flows?.data?.find((f) => f.id === displayFlowForBadge.duplicated_origin_id)?.version || "?"})`
                          : ""
                      }`
                    : "En construcción v1"}
              </Badge> */}
              {isVersionPending ? (
                <Badge size="sm" variant="neutral">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin mr-1"
                  />
                  Creando nueva versión...
                </Badge>
              ) : (
                <div className="inline-flex items-center">
                  <Badge
                    size="sm"
                    variant={
                      displayFlowForBadge
                        ? getFlowStatusBadgeColor(displayFlowForBadge?.status)
                        : "action"
                    }
                    onClick={() => {
                      if (canEdit && canViewFlow) {
                        setModalState("versioning");
                      }
                    }}
                    className={
                      publishedFlow &&
                      publishedFlow.version !== displayFlowForBadge?.version
                        ? "rounded-r-none"
                        : ""
                    }
                  >
                    {displayFlowForBadge
                      ? `${getFlowStatusLabel(displayFlowForBadge.status)} v${displayFlowForBadge.version || 1}${
                          displayFlowForBadge.duplicated_origin_id &&
                          flows?.data?.find(
                            (f) =>
                              f.id === displayFlowForBadge.duplicated_origin_id,
                          )?.version !==
                            (displayFlowForBadge.version || 1) - 1
                            ? ` (Restaurada de v${flows?.data?.find((f) => f.id === displayFlowForBadge.duplicated_origin_id)?.version || "?"})`
                            : ""
                        }`
                      : "En construcción v1"}
                  </Badge>
                  {publishedFlow &&
                    publishedFlow.version !== displayFlowForBadge?.version && (
                      <Badge
                        size="sm"
                        variant="success"
                        onClick={() => {
                          setSelectedVersion(publishedFlow.version || null);
                        }}
                        className="rounded-l-none -ml-px cursor-pointer"
                      >
                        Activa v{publishedFlow.version}
                      </Badge>
                    )}
                </div>
              )}
              {/* {publishedFlow &&
                publishedFlow.version !== displayFlowForBadge?.version && (
                  <Badge
                    size="sm"
                    variant="success"
                    onClick={() => {
                      setSelectedVersion(publishedFlow.version || null);
                    }}
                    className="rounded-l-none -ml-px cursor-pointer"
                  >
                    Activa v{publishedFlow.version}
                  </Badge>
                )} */}
            </div>
            {/* tag */}
            {process?.data &&
              process.data.tags &&
              process.data.tags.length > 0 && (
                <div className="flex flex-row items-center gap-2">
                  <Badge
                    variant="neutral"
                    shape="square"
                    className="h-8 p-0 min-w-8 flex justify-center rounded-lg"
                  >
                    <Icon className="text-body" type="far" name="tag" />
                  </Badge>
                  <span className="text-body-loud">
                    {process.data.tags[0]?.name}
                  </span>
                </div>
              )}

            {/* tds */}
            {canEdit && (
              <Button
                size="xs"
                variant="quiet"
                onClick={() => {
                  setModalState("tds");
                }}
              >
                Ficha técnica{" "}
                {process?.data?.files?.length
                  ? "v" + process?.data?.files?.length
                  : null}{" "}
                <Icon type="far" name="file" />
              </Button>
            )}
            {canEdit && (
              <Button
                size="xs"
                variant="quiet"
                onClick={() => {
                  setModalState("details");
                }}
              >
                Detalles en portal{" "}
              </Button>
            )}
          </div>
          <p className="text-size-body text-body">
            <p
              className="text-size-body text-body rich-text-content"
              dangerouslySetInnerHTML={{
                __html: process?.data?.description || "",
              }}
            ></p>
          </p>

          <div className="text-size-body-sm">
            <span className="text-body-quiet">
              Última actualización{" "}
              {formatToRelativeTime(process?.data.updated_at || "")} | Creado el{" "}
              {formatDate(process?.data?.created_at || "")} por
            </span>{" "}
            <span className="text-action">{userName}</span>
          </div>
          {currentRevision &&
            (currentRevision.status === "APPROVED" ||
              currentRevision.status === "DENIED") &&
            currentRevision.comments?.[0] && (
              <ResolutionComment
                status={currentRevision.status}
                comment={currentRevision.comments[0].text}
                createdAt={currentRevision.comments[0].created_at}
                createdBy={currentRevision.comments[0].created_by}
              />
            )}
        </div>
      )}
      {canViewFlow && (
        <Renderify
          condition={isError}
          elseShow={
            <Renderify
              condition={isLoadingFlows && !flows}
              elseShow={
                <>
                  <Renderify condition={!!isEmpty}>
                    <div className="flex flex-col gap-6 items-center justify-center flex-1 min-h-100">
                      <div>
                        <Heading className="text-center" variant="h4">
                          Aún no tienes flujos
                        </Heading>
                        <Body className="text-body-quiet text-center">
                          Agrega un flujo para empezar a configurar el proceso
                        </Body>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setModalState("new-parent-flow");
                        }}
                      >
                        <span className="hidden md:inline-block">
                          Crear flujo
                        </span>
                        <Icon type="far" name="plus" className="text-current" />
                      </Button>
                    </div>
                  </Renderify>
                  <Renderify condition={!isEmpty}>
                    <div className="flex justify-between items-center h-9">
                      <div className="flex gap-2 items-center h-full">
                        <SortDropdown
                          currentOrderField={order_by || "created_at"}
                          currentOrderDirection={orderDirection}
                          sortOptions={sortFields}
                          onClick={handleapplySort}
                          buttonSize="sm"
                        />
                        <Button
                          data-testid="filter-button"
                          size="sm"
                          variant="quiet"
                          type="button"
                          onClick={() => {
                            setModalState("filters");
                          }}
                          className="relative"
                        >
                          <span className="hidden md:inline-block">
                            Filtros
                          </span>
                          <Icon
                            type="far"
                            name="filter"
                            className="text-current"
                          />
                          <PulseIndicator showIndicator={filtersCount > 0} />
                        </Button>
                        {isLoadingFlows && (
                          <Icon
                            type="far"
                            name="spinner-third"
                            className="text-on-disabled animate-spin"
                          />
                        )}
                      </div>
                      <div className="flex gap-2 items-center h-full">
                        <SearchInput
                          variant="sm"
                          placeholder={"Buscar flujo..."}
                          data-testid="search-input"
                          className="w-60 text-sm hidden md:inline"
                          value={search || ""}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                        {canCreateFlow && !isVersionLocked && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setModalState("new-flow");
                            }}
                            variant="secondary"
                          >
                            <span className="hidden md:inline-block">
                              Crear flujo
                            </span>
                            <Icon
                              type="far"
                              name="plus"
                              className="text-current"
                            />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Renderify>
                  <Renderify
                    condition={!!flows?.data && flows.data.length > 0}
                    elseShow={
                      hasActiveSearch ? (
                        <div className="flex flex-col gap-6 items-center justify-center flex-1 min-h-100">
                          <div>
                            <Heading className="text-center" variant="h4">
                              Búsqueda sin resultados
                            </Heading>
                            <Body className="text-body-quiet text-center">
                              No se encontraron flujos que coincidan con los
                              criterios de búsqueda
                            </Body>
                          </div>
                        </div>
                      ) : null
                    }
                  >
                    <div className="pt-4 flex flex-col gap-2">
                      {/* {isVersionPending ? (
                        <div className="flex flex-col gap-4 items-center justify-center min-h-50">
                          <Icon
                            type="far"
                            name="spinner-third"
                            className="text-body-quiet text-2xl animate-spin"
                          />
                          <p className="text-body-quiet">
                            Creando versión {selectedVersion}...
                          </p>
                        </div>
                      ) : ( */}
                      {isVersionPending ? (
                        <div className="flex flex-col gap-4 items-center justify-center min-h-50">
                          <Icon
                            type="far"
                            name="spinner-third"
                            className="text-body-quiet text-2xl animate-spin"
                          />
                          <p className="text-body-quiet">
                            Creando nueva versión...
                          </p>
                          <p className="text-body-quiet text-sm text-center">
                            Este proceso puede tardar unos momentos.
                          </p>
                        </div>
                      ) : (
                        displayFlows.map((flow) => (
                          <ParentFlowCard
                            key={flow.id}
                            data={flow}
                            processStatus={process?.data.status}
                            handleDuplicateFlow={handleDuplicateFlow}
                            onNewVersion={(d) =>
                              handleDuplicateFlow(d, "version")
                            }
                            onEdit={(flowId: string) => {
                              navigate(
                                `/procesos/${processId}/flujo/${flowId}`,
                              );
                            }}
                            onEditSubFlow={(flowId: string) => {
                              setFlowToEdit(flowId);
                              setModalState("edit-subflow");
                            }}
                            onViewExecutions={() => {
                              navigate(
                                `/procesos/${processId}/flujo/${flow.id}/ejecuciones`,
                              );
                            }}
                            onDeleteFlow={(flowId: string) => {
                              setFlowIdToDelete(flowId);
                              setModalState("delete");
                            }}
                            onStatusChange={handleFlowStatusChange}
                            isVersionLocked={isVersionLocked}
                          />
                        ))
                      )}
                    </div>
                  </Renderify>
                </>
              }
            >
              <div className="flex flex-col gap-6 items-center justify-center flex-1 min-h-100">
                <Icon
                  name="spinner-third"
                  type="fas"
                  className="animate-spin text-on-disabled"
                />
              </div>
            </Renderify>
          }
        >
          <div className="flex flex-col gap-6 items-center justify-center flex-1 min-h-100">
            <p className="text-danger">Error al cargar flujos</p>
          </div>
        </Renderify>
      )}
      {flowIdToDelete && modalState === "delete" && (
        <DeleteFlowModal
          flowId={flowIdToDelete}
          isOpen={modalState === "delete"}
          onClose={() => {
            setModalState("idle");
            setFlowIdToDelete(null);
          }}
        />
      )}
      {flowToDuplicate &&
        modalState === "duplicate-flow" &&
        duplicationType && (
          <DuplicateFlowModal
            flowId={flowToDuplicate}
            isOpen={modalState === "duplicate-flow"}
            duplicationType={duplicationType}
            onClose={() => {
              setModalState("idle");
              setFlowToDuplicate(null);
            }}
          />
        )}
      {modalState === "new-version" && flows?.data?.[0]?.id && (
        <NewFlowVersionModal
          flowId={latestVersionFlow?.id || ""}
          latestVersion={latestVersion}
          isOpen={modalState === "new-version"}
          onClose={() => setModalState("idle")}
          onSuccess={(newVersion) => {
            setIsWaitingForVersion?.(true);
            setSelectedVersion(newVersion);
          }}
        />
      )}
      {modalState === "edit-subflow" && flowToEdit && (
        <EditSubFlowModal
          open
          onClose={() => {
            setModalState("idle");
            setFlowToEdit(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["flows", processId] });
          }}
          flowId={flowToEdit}
          //processId={processId!}
          //currentVersion={selectedVersion ?? latestVersion}
          availableParents={allFlowsInVersion}
        />
      )}
      {modalState === "restore-version" && flowToRestore && (
        <RestoreVersionModal
          flowId={flowToRestore}
          latestVersion={latestVersion}
          isOpen={modalState === "restore-version"}
          onClose={() => {
            setIsWaitingForVersion?.(true);
            setModalState("idle");
            setFlowToRestore(null);
          }}
          onSuccess={(newVersion) => {
            setSelectedVersion(newVersion);
          }}
        />
      )}
    </>
  );
};
